import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from "react-router-dom";
import { toastr } from 'react-redux-toastr';
import { useTranslation } from 'react-i18next';

import CustomButton from '../../../CustomButton';

import { getUserInfo } from '../../../../../../shared/api/action/ClientActions';
import { loginUser, logoutUser } from '../../../../../../shared/api/action/AccountActions';
import { useAuth } from "../../../../../../app/providers/AuthContext";
import { isRememberMeEnabled } from '../../../../../../shared/constants/featureFlags';

import '../../../dump/menuComponents/menuComponents.sass';

const REMEMBER_ME_MAX_AGE = 31 * 24 * 60 * 60; // seconds, how long the login is kept on the web (client-side only)

const readStoredRememberMe = () => localStorage.getItem('r_u') === null ? 1 : localStorage.getItem('r_u') * 1;

// Remember Me only: localStorage can throw in restricted WebViews, so a failure falls back to the default
const readRememberMe = (rememberMeEnabled) => {
	if (!rememberMeEnabled) return readStoredRememberMe();
	try {
		return localStorage.getItem('r_u') === '0' ? 0 : 1;
	} catch (e) {
		return 1;
	}
};

const saveRememberMe = (rememberMeEnabled, value) => {
	if (!rememberMeEnabled) return;
	try {
		localStorage.setItem('r_u', value);
	} catch (e) {
		// ignore: the choice still applies to this login, it just isn't remembered for the next one
	}
};

const LoginForm = (props) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const rememberMeEnabled = isRememberMeEnabled();
	const { login, logout } = useAuth();
	const { t } = useTranslation('translation', { keyPrefix: 'loginForms.loginForm' });
	const [data, setData] = useState({
		email: '',
		password: '',
	})
	const [errMessage, setErrMessage] = useState({
		email: '',
		password: '',
	})
	const [toggle_password, setToggle_password] = useState(true)
	const [rememberMe, setRememberMe] = useState(1)

	useEffect(() => {
		setRememberMe(readRememberMe(rememberMeEnabled));
	}, []);

	const toggleLoader = useCallback((show) => {
		dispatch({ type: 'TOGGLE_LOADER', payload: show });
	}, [dispatch]);

	const togglePassword = () => {
		setToggle_password(!toggle_password);
	}

	const fillForm = ({ target }) => {
		const value = target.type === 'checkbox' ? target.checked : target.value;

		if (target.name === 'rememberMe') {
			setRememberMe(value * 1);
			saveRememberMe(rememberMeEnabled, value * 1);
		} else {
			let dataUpdate = data;
			dataUpdate[target.name] = value;
			setData({ ...dataUpdate, [target.name]: value });
			setErrMessage({ ...errMessage, email: '', password: '' });
		}
	}

	const checkForm = (data) => {
		let error = false;
		const updatedErrMessage = { ...errMessage };

		for (const i in data) {
			if (data[i] == '') {
				error = true;
				updatedErrMessage[i] = t('errEnterYour') + i;
			}
			else if (errMessage[i]) {
				updatedErrMessage[i] = null;
			}
		}

		if (!updatedErrMessage.email) {
			const re = /^([a-zA-Z0-9_\-\.\+]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			if (!re.test(data.email)) {
				error = true;
				updatedErrMessage.email = t('errEmail');
			}
		}

		setErrMessage(updatedErrMessage);
		return error;
	}

	const sendForm = (e) => {
		e.preventDefault()

		const error = checkForm(data)

		if (!error) {
			toggleLoader(true);

			loginUser(data, rememberMeEnabled ? !!rememberMe : undefined)
				.then(async (res, err) => {
					if (res.data.response.error && res.data.response.error === 'you have to logout first') {
						logoutUser()
							.then(async (res, err) => {
								await logout();
								sendForm(e);
							})
							.catch((error) => {
								console.log('!error');
								console.log(error);
							});
					}
					else if (res.data.response.error && ['Invalid password', 'Specified user does not exist'].includes(res.data.response.error)) {
						toggleLoader(false)
						setErrMessage({ ...errMessage, password: t('errEmail/Password') })
					}
					else if (res.data.response.error) {
						toggleLoader(false)
						toastr.error(t('unableLoginUser'));
						console.log("Error: ", res.data)
					}
					else if (res.data.response.roles && res.data.response.roles[0] === 'notification_recipient') {
						toggleLoader(false)
						navigate('/confirmation');
					}
					else {
						// ToDo: Cognito - disable
						const { session_token } = res.data.response
						// const { auth_refresh_token, auth_token } = res.data.response;
						// await login({ auth_refresh_token, auth_token });
						// Remember Me is handled entirely on the client: checked keeps the login across restarts
						// (web: cookie with maxAge, native: Preferences), unchecked keeps it for this session only
						const storageOptions = !rememberMeEnabled ? undefined : rememberMe ? { maxAge: REMEMBER_ME_MAX_AGE } : { persist: false };
						await login({ session_token }, storageOptions);
						// FWSD-6093, checks if EULA was accepted otherwise redirect to /eula-agreement
						getUserInfo()
							.then(resUserInfo => {
								toggleLoader(false);
								if (resUserInfo.data.response.user.eula_was_viewed) {
									navigate('/', { replace: true });
								} else {
									navigate('/eula-agreement', {
										replace: true,
										state: {
											isLoginMode: true
										}
									});
								}
							})
							.catch(errorUserInfo => {
								toggleLoader(false)
								console.log('getUserInfo error: ', errorUserInfo.response, errorUserInfo)
								toastr.error(errorUserInfo)
							})
					}
				})
				.catch((error) => {
					console.log(error.response);

					toggleLoader(false)

					if (error.response && error.response.data.response.error) {
						let error_content = error.response.data.response.error;
						let error_title = '';
						if (error_content.toLowerCase() === 'invalid password') {
							error_title = t('errorTitle_1');
							error_content = t('errorContent_1');
						}
						else if (error_content.toLowerCase() === 'specified user does not exist') {
							error_title = t('errorTitle_2');
							error_content = t('errorContent_2');
						}

						toastr.error(error_content);
					}
					else {
						toastr.error(t('unableLoginUser'));
					}
				});
		}
	}

	const { email, password } = errMessage;

	return (
		<div className="panel-body">
			<h3 className="login-header">{t('headerLogin')}</h3>
			<div className="form-wrapper">

				<form onSubmit={sendForm} autoComplete={rememberMe ? 'auto' : 'off'} id="login-form">
					{!rememberMe && <input autoComplete="false" name="hidden" type="text" className="hidden" hidden="none" />}
					<div className='login-form'>
						<label className={`login-label ${(data.email.length) ? 'active' : ''}`}>{t('labelE-mail')}</label>
						<input
							type="text"
							name="email"
							className={`vehicle-search ${(email || password) ? 'error' : ''}`}
							onChange={fillForm}
							placeholder={t('placeholderE-Mail')}
							autoComplete={rememberMe ? 'auto' : 'new-password'}
						/>
						{email && <span className="help-block error-label">{email}</span>}
					</div>
					<div className="login-form has-feedback">
						<label className={`login-label ${(data.password.length) ? 'active' : ''}`}>{t('labelPassword')}</label>
						<input
							type={toggle_password ? 'password' : 'text'}
							id="password"
							name="password"
							className={`vehicle-search ${password ? 'error' : ''}`}
							data-toggle="password"
							onChange={fillForm}
							placeholder={t('placeholderPassword')}
							autoComplete={rememberMe ? 'auto' : 'new-password'}
						/>
						<span
							className={`fas ${toggle_password ? 'fa-eye' : 'fa-eye-slash'} form-control-feedback`}
							onClick={togglePassword}
						/>
						{password && <span className="help-block error-label">{password}</span>}
					</div>
					<div className="login-form">
						<div className="checkbox">
							<label>
								<input
									type="checkbox"
									name="rememberMe"
									checked={rememberMeEnabled ? !!rememberMe : undefined}
									onChange={fillForm}
								/>
								{t('labelRememberMe')}
							</label>
						</div>
					</div>
					<div className="login-form submit-btn">
						<CustomButton variant="primary" type="submit" value="Login" id="login-button" style={{ padding: '8px 0px' }}>{t('btnLogin')}</CustomButton>
					</div>

					<div className="forgot-password">
						<Link to="/restore-password">
							{t('linkForgotPassword')}
						</Link>
					</div>
				</form>

			</div>
		</div>
	)
}

export default LoginForm;
